/** Workspace invites awaiting a response: accept or decline, right in the sidebar. */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { supabase } from '../sync/useSync';
import { refreshWorkspacesSoon, usePendingInvites } from './state';

export function PendingInvites() {
  const { t } = useTranslation();
  const invites = usePendingInvites();
  const [busy, setBusy] = useState<string | null>(null);

  if (invites.length === 0) return null;

  const respond = async (workspaceId: string, accept: boolean) => {
    setBusy(workspaceId);
    try {
      await supabase().functions.invoke('workspace-members', {
        body: { action: 'respond', workspaceId, accept },
      });
      refreshWorkspacesSoon();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-1 px-2">
      <h2 className="px-1 pb-1 text-xs font-medium tracking-wide text-fg-muted uppercase">
        {t('workspaces.invitesTitle')}
      </h2>
      {invites.map((w) => (
        <div
          key={w.id}
          className="flex items-center gap-1.5 rounded-lg border border-border bg-bg-subtle px-2 py-1.5 text-sm"
        >
          <span className="min-w-0 flex-1 truncate">
            {t('workspaces.invitedTo', { name: w.name })}
          </span>
          <button
            type="button"
            title={t('workspaces.acceptInvite')}
            aria-label={t('workspaces.acceptInvite')}
            disabled={busy === w.id}
            onClick={() => void respond(w.id, true)}
            className="grid size-6 shrink-0 place-items-center rounded-md text-fg-muted hover:bg-bg-hover hover:text-accent-ink"
          >
            <Check className="size-4" />
          </button>
          <button
            type="button"
            title={t('workspaces.declineInvite')}
            aria-label={t('workspaces.declineInvite')}
            disabled={busy === w.id}
            onClick={() => void respond(w.id, false)}
            className="grid size-6 shrink-0 place-items-center rounded-md text-fg-muted hover:bg-bg-hover hover:text-danger"
          >
            <X className="size-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
